import { db, storage, functions, serverTimestamp } from '@/firebase'
import {
  collection,
  doc,
  increment,
  setDoc,
  updateDoc,
} from 'firebase/firestore'
import { ref as sRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { httpsCallable } from 'firebase/functions'
import { getUserDisplayName, sanitizeFileName } from '@/services/userProfile'
import { isInactiveStatus } from '@/services/requests'

function buildBaseCaseDoc({ form, user }) {
  return {
    createdBy: user.uid,
    createdByEmail: user.email ?? null,
    submissionType: form.submissionType,
    submissionName: form.submissionName,
    protocolTitle: form.protocolTitle,
    sponsor: form.sponsor,
    protocolNumber: form.protocolNumber,
    submitPI: form.submitPI,
    hasFinancialDisclosure: form.hasFinancialDisclosure,
    status: form.status ?? 'Submitted',
    attachmentsCount: 0,
    assignedToUid: null,
    assignedToName: null,
    assignedToEmail: null,
    assignedAt: null,
    assignedByUid: null,
    assignedByName: null,
    assignedByEmail: null,
    notInterestedEmployeeIds: [],
    closedAt: null,
    closedByUid: null,
    closedByName: null,
    closedByEmail: null,
    submittedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastUpdatedBy: user.uid,
    version: 1,
  }
}

function wrapCaseActionError(error, fallbackMessage) {
  const code = String(error?.code || '')
  const message = String(error?.message || '').trim()

  if (code === 'functions/permission-denied') {
    return new Error('You do not have permission to update this case.')
  }

  if (code === 'functions/not-found') {
    if (/case not found/i.test(message)) {
      return new Error('This case could not be found.')
    }
    return new Error('The latest case action service is not deployed yet. Deploy Firebase Functions and try again.')
  }

  return new Error(message || fallbackMessage)
}

async function callCaseAction(name, payload, fallbackMessage) {
  const fn = httpsCallable(functions, name)
  try {
    await fn(payload)
  } catch (error) {
    throw wrapCaseActionError(error, fallbackMessage)
  }
}

export async function createCaseWithFiles({ form, files = [], user }) {
  if (!user || !user.uid) throw new Error('User must be signed in')

  const caseRef = doc(collection(db, 'cases'))
  const caseId = caseRef.id
  await setDoc(caseRef, {
    ...buildBaseCaseDoc({ form, user }),
    caseId,
  })

  const uploads = (files || []).map(async (file) => {
    if (!file) return
    const filename = `${Date.now()}_${sanitizeFileName(file.name)}`
    const storagePath = `cases/${caseId}/files/${filename}`
    const fileRef = sRef(storage, storagePath)
    await uploadBytes(fileRef, file, { contentType: file.type || undefined })
    const downloadURL = await getDownloadURL(fileRef)
    const metaRef = doc(collection(db, 'cases', caseId, 'files'))
    await setDoc(metaRef, {
      name: file.name,
      storedName: filename,
      size: file.size,
      contentType: file.type || null,
      storagePath,
      downloadURL,
      uploadedBy: user.uid,
      uploadedByEmail: user.email ?? null,
      uploadedAt: serverTimestamp(),
    })
  })

  const results = await Promise.allSettled(uploads)
  const successCount = results.filter((result) => result.status === 'fulfilled').length

  await updateDoc(caseRef, {
    attachmentsCount: successCount,
    updatedAt: serverTimestamp(),
    lastUpdatedBy: user.uid,
  })

  return caseId
}

export async function finalizeCase(tempId, form) {
  const fn = httpsCallable(functions, 'finalizeCase')

  try {
    const res = await fn({ tempId, form })
    const caseId = res?.data?.caseId
    if (!caseId) throw new Error('Missing caseId in finalizeCase response')
    return caseId
  } catch (e) {
    const code = e?.code || 'functions/internal'
    const msg = e?.message || 'Callable failed'
    const details = e?.details ? ` (${JSON.stringify(e.details)})` : ''
    throw new Error(`${code}: ${msg}${details}`)
  }
}

export async function assignCaseToCurrentEmployee({ caseId, user, profile }) {
  if (!caseId) throw new Error('Case ID is required.')
  if (!user?.uid) throw new Error('User must be signed in.')
  await callCaseAction('assignCaseToCurrentEmployee', { caseId }, 'Unable to assign this case right now.')
}

export async function markCaseNotInterested({ caseId, user }) {
  if (!caseId) throw new Error('Case ID is required.')
  if (!user?.uid) throw new Error('User must be signed in.')
  await callCaseAction('markCaseNotInterested', { caseId }, 'Unable to hide this case right now.')
}

export async function updateCaseStatus({ caseId, newStatus, user, profile }) {
  if (!caseId) throw new Error('Case ID is required.')
  if (!newStatus) throw new Error('Status is required.')
  if (!user?.uid) throw new Error('User must be signed in.')

  const payload = {
    status: newStatus,
    updatedAt: serverTimestamp(),
    lastUpdatedBy: user.uid,
  }

  if (isInactiveStatus(newStatus)) {
    payload.closedAt = serverTimestamp()
    payload.closedByUid = user.uid
    payload.closedByEmail = user.email ?? null
    payload.closedByName = getUserDisplayName(profile, user)
  } else {
    payload.closedAt = null
    payload.closedByUid = null
    payload.closedByEmail = null
    payload.closedByName = null
  }

  await updateDoc(doc(db, 'cases', caseId), payload)
}

export async function uploadCaseAttachment({ caseId, file, user }) {
  if (!caseId) throw new Error('Case ID is required.')
  if (!file) throw new Error('A file is required.')
  if (!user?.uid) throw new Error('User must be signed in.')

  const filename = `${Date.now()}_${sanitizeFileName(file.name)}`
  const storagePath = `cases/${caseId}/files/${filename}`
  const fileRef = sRef(storage, storagePath)

  await uploadBytes(fileRef, file, { contentType: file.type || undefined })
  const downloadURL = await getDownloadURL(fileRef)

  const metaRef = doc(collection(db, 'cases', caseId, 'files'))
  await setDoc(metaRef, {
    name: file.name,
    storedName: filename,
    size: file.size,
    contentType: file.type || null,
    storagePath,
    downloadURL,
    uploadedBy: user.uid,
    uploadedByEmail: user.email ?? null,
    uploadedAt: serverTimestamp(),
  })

  await updateDoc(doc(db, 'cases', caseId), {
    attachmentsCount: increment(1),
    updatedAt: serverTimestamp(),
    lastUpdatedBy: user.uid,
  })

  return { downloadURL, storagePath }
}
